import type { ReactNode } from "react";
import { STATUS_LABELS } from "../labels.ts";
import { piePath, statusMeta, type StatusKind, type Tone } from "./status-kinds.ts";

// Status, chips and results of the new interface (docs/design-system.md §0.8):
// a status is always an icon and a word, never colour alone.

const MARK: Partial<Record<string, string>> = {
  check: "M5.2 8.2l1.9 1.9 3.7-4.1",
  dash: "M5.2 8h5.6",
  down: "M8 4.8v6M5.6 8.6 8 11l2.4-2.4",
  question: "M6.3 6.2a1.9 1.9 0 1 1 2.4 1.9c-.5.2-.7.5-.7 1v.3M8 11.4v.1",
  cross: "M5.8 5.8l4.4 4.4M10.2 5.8l-4.4 4.4",
};

/** The status glyph alone; `label` overrides the status word read by screen readers. */
export function StatusIcon({ kind, progress, size = 14, label }: { kind: StatusKind; progress?: number | null; size?: number; label?: string }) {
  const meta = statusMeta(kind, progress);
  const name = label ?? STATUS_LABELS[kind];
  let body: ReactNode;
  if (meta.shape === "dashed") body = <circle cx="8" cy="8" r="6.25" className="z-st-ring" strokeDasharray="2.2 2" />;
  else if (meta.shape === "ring") body = <circle cx="8" cy="8" r="6.25" className="z-st-ring" />;
  else if (meta.shape === "pie") {
    const path = piePath(meta.progress ?? 0);
    body = <><circle cx="8" cy="8" r="6.25" className="z-st-ring" />{path === "full" ? <circle cx="8" cy="8" r="3.6" className="z-st-fill" /> : path && <path d={path} className="z-st-fill" />}</>;
  } else if (meta.shape === "due") body = <><circle cx="8" cy="8" r="7" className="z-st-fill" /><circle cx="8" cy="8" r="2.6" className="z-st-cut" /></>;
  else if (meta.shape === "pause") body = <><circle cx="8" cy="8" r="6.25" className="z-st-ring" /><path d="M6.5 5.8v4.4M9.5 5.8v4.4" className="z-st-line" /></>;
  else body = <><circle cx="8" cy="8" r="7" className="z-st-fill" /><path d={MARK[meta.shape]} className="z-st-mark" /></>;
  // An empty label means the word is already next to the icon: the icon is then hidden from screen readers.
  if (name === "") return <svg className="z-st" data-tone={meta.tone} width={size} height={size} viewBox="0 0 16 16" aria-hidden>{body}</svg>;
  return <svg className="z-st" data-tone={meta.tone} width={size} height={size} viewBox="0 0 16 16" role="img" aria-label={name}><title>{name}</title>{body}</svg>;
}

/**
 * A short property as a label. `outline` (default) is neutral with a hairline;
 * `soft` fills it with the hue's soft colour, for a state worth noticing.
 */
export function Chip({ tone = "gray", variant = "outline", icon, dot, status, progress, inline, children }: {
  tone?: Tone; variant?: "outline" | "soft"; icon?: ReactNode; dot?: boolean; status?: StatusKind; progress?: number | null;
  /** Smaller, to sit in running text: a reference in a playbook's skill text (§0.6). */
  inline?: boolean; children: ReactNode;
}) {
  return <span className={`z-chip is-${variant}${inline ? " is-ref" : ""}`} data-tone={tone}>
    {status ? <StatusIcon kind={status} progress={progress} size={12} label="" /> : dot ? <i className="z-chip-dot" aria-hidden /> : icon}
    {children}
  </span>;
}

/** A status as a chip: its icon and word, softly filled in its hue when `soft`. */
export function StatusChip({ kind, progress, soft, children }: { kind: StatusKind; progress?: number | null; soft?: boolean; children?: ReactNode }) {
  const tone = statusMeta(kind).tone;
  return <Chip status={kind} progress={progress} variant={soft ? "soft" : "outline"} tone={soft ? tone : "gray"}>{children ?? STATUS_LABELS[kind]}</Chip>;
}

const RESULT_KIND = { positive: "positive", none: "none", negative: "negative", inconclusive: "inconclusive" } as const;

/** A confirmed reading: the result's icon and word, filled in its hue. */
export function ResultBadge({ result, operational }: { result: keyof typeof RESULT_KIND; operational?: boolean }) {
  const kind = operational && result === "positive" ? "accepted" : RESULT_KIND[result];
  return <StatusChip kind={kind} soft />;
}
